import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import SidebarExtension from "./SidebarExtension";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface MiniSidebarSectionProps {
  icon: React.ReactNode;
  label: string;
  isActive?: boolean;
  children?: React.ReactNode;
}

const MiniSidebarSection: React.FC<MiniSidebarSectionProps> = ({
  icon,
  label,
  isActive,
  children,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <Tooltip open={isHovered && !isOpen}>
          <TooltipTrigger asChild>
            <Button
              ref={buttonRef}
              variant="ghost"
              size="icon"
              className={`w-12 h-12 relative group rounded-full text-muted-foreground hover:text-foreground dark:hover:text-gray-200 hover:bg-sky-100 ${
                isActive ? "bg-sky-100" : ""
              } ${isOpen ? "bg-sky-100 text-foreground dark:text-gray-200" : ""}`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => setIsOpen(true)}
              aria-label={label}
            >
              <div className="flex items-center">
                <span
                  className={`transition-colors ${
                    isActive || isOpen
                      ? "text-sky-500"
                      : "text-muted-foreground group-hover:text-foreground dark:group-hover:text-gray-200"
                  }`}
                >
                  {icon}
                </span>
                {(isHovered || isOpen) && (
                  <ChevronRight
                    size={14}
                    className="absolute right-0 text-gray-400"
                  />
                )}
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={10}
            align="center"
            className="-translate-y-[40%]"
          >
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <SidebarExtension
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={label}
        triggerRef={buttonRef}
      >
        <div className="py-2 min-w-[240px]">{children}</div>
      </SidebarExtension>
    </>
  );
};

export default MiniSidebarSection;
